import React, { useState, useEffect } from "react";
import axios from "axios";
import Elipse1 from "../assets/images/Ellipse 1.png";
import Elipse2 from "../assets/images/Ellipse 2.png";
import Globe from "../assets/images/Globe.png";
import Icon from "../assets/images/Icon.png";
import { useNavigate, useLocation } from "react-router-dom";
import logoutIcon from "../assets/svg/logout-icon.svg";
import { MdArrowBack } from "react-icons/md";
import useToken from "./useToken";

const PromptSubmission = ({ userID, closeModal }) => {
  document.title = "Social Glass AI Post Submission";

  const [prompts, setPrompts] = useState([]);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [selectedPromptDetails, setSelectedPromptDetails] = useState(null);
  const [postURLs, setPostURLs] = useState([]);
  const [submitStatus, setSubmitStatus] = useState("");
  const [newURL, setNewURL] = useState("");
  const [hasPermission, setHasPermission] = useState(null);
  const [isError, setIsError] = useState(false);
  const [duplicatePosts, setDuplicatePosts] = useState([]);
  const [successfullySubmittedPosts, setSuccessfullySubmittedPosts] = useState(
    [],
  );

  const [invalidPosts, setInvalidPosts] = useState([]);
  const navigate = useNavigate();
  const { token } = useToken();
  const location = useLocation();

  const handleCancel = () => {
    setPostURLs([]); // clear all uploaded URLs
    setNewURL(""); // Clear the input field
  };

  const handleClose = () => {
    if (typeof closeModal === "function") {
      closeModal(); // Close the modal
    }
    localStorage.removeItem("authToken"); // Clear the auth token
    navigate("/"); // Redirect to the home page
  };

  const fetchPrompts = async () => {
    try {
      const token = localStorage.getItem("token");

      if (!token) {
        console.error("No token found, user must log in");
        return;
      }

      const response = await axios.get(
        "https://genz-staging.feeltiptop.com/api/user/prompts",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setPrompts(response.data);

      const selected = response.data.find((prompt) => prompt.currentlySelected);
      if (selected) {
        setSelectedPrompt(selected.promptID);
        setSelectedPromptDetails(selected);
      }
    } catch (error) {
      console.error("Error fetching prompts:", error);
    }
  };

  useEffect(() => {
    fetchPrompts();
    if (token) {
      checkPermission();
    }
  }, [token]);

  const checkPermission = async () => {
    try {
      const response = await axios.get(
        "https://genz-staging.feeltiptop.com/api/social_glass/SocialGlassGuard",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setHasPermission(response.data.success);
      console.log("has permission: ", response.data.success);
    } catch (err) {
      setHasPermission(false);
    }
  };

  const handlePromptClick = (promptID) => {
    const selected = prompts.find((prompt) => prompt.promptID === promptID);
    setSelectedPrompt(promptID);
    setSelectedPromptDetails(selected);
    setIsError(false);
    setDuplicatePosts([]);
    setInvalidPosts([]);
    setSuccessfullySubmittedPosts([]);
    setPostURLs([]);
    setSubmitStatus("");
  };

  const handleURLChange = (e, index) => {
    const updatedURLs = [...postURLs];
    updatedURLs[index] = e.target.value;
    setPostURLs(updatedURLs);
  };

  const handleUpload = () => {
    const trimmedURL = newURL.trim();
    // Normalize HTTP to HTTPS
    const normalizedURL = trimmedURL.startsWith("http:")
      ? trimmedURL.replace(/^http:/, "https:")
      : trimmedURL;
    console.log(normalizedURL);
    if (isValidSocialMediaURL(normalizedURL)) {
      setPostURLs([...postURLs, normalizedURL]);
      setNewURL("");
      setSubmitStatus("");
      setIsError(true);
    } else {
      setSubmitStatus(
        "Enter a valid Instagram, YouTube Shorts, X (Twitter), or TikTok video/post URL.",
      );
      setIsError(true);
      setTimeout(() => {
        setSubmitStatus("");
      }, 5000);
    }
  };

  const handleRemoveURL = (index) => {
    const updatedURLs = postURLs.filter((_, idx) => idx !== index);
    setPostURLs(updatedURLs);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!selectedPrompt) {
      setSubmitStatus("Please select a prompt before submitting.");
      setIsError(true);
      return;
    }

    if (
      postURLs.length === 0 ||
      postURLs.some((url) => !isValidSocialMediaURL(url))
    ) {
      setSubmitStatus(
        "Please enter valid URLs from Instagram, YouTube, X (Twitter), or TikTok.",
      );
      setIsError(true);
      return;
    }

    const postContent = postURLs.map((url) => ({ PostContent: url }));

    try {
      const token = localStorage.getItem("token", "adsfsa");
      const response = await axios.post(
        "https://genz-staging.feeltiptop.com/api/user/submitPost",
        {
          userID,
          promptID: selectedPrompt,
          PostContent: postContent,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = response.data;
      console.log("data being returned for submitting posts: ", data);

      setSubmitStatus(data.message);

      if (
        data.message === "All posts were duplicates" ||
        data.message === "Some posts submitted successfully!" ||
        data.message === "All posts were duplicates or invalid"
      ) {
        setIsError(true);
        setDuplicatePosts(data.skipped_duplicates);
        setInvalidPosts(data.skipped_invalid);
      } else {
        setIsError(false);
        setPostURLs([]);
        // ⏳ Reset message after 5 seconds
      }

      setSuccessfullySubmittedPosts(data.inserted);

      setTimeout(() => {
        setSubmitStatus("");
      }, 5000);
    } catch (error) {
      console.error("Error submitting posts:", error);
      setIsError(true);
      if (error.response?.status === 401) {
        setSubmitStatus("Session expired. Please login again.");
        // You can also redirect the user to the login page here
        // window.location.href = '/login';
      } else if (error.response?.data?.error) {
        setSubmitStatus(error.response.data.error);
      } else {
        setSubmitStatus(
          "There was an error submitting your posts, please try again.",
        );
      }
      setTimeout(() => {
        setSubmitStatus("");
      }, 5000);
    }
  };

  const isValidSocialMediaURL = (url) => {
    try {
      const parsedUrl = new URL(url);
      const protocol = parsedUrl.protocol.toLowerCase();
      const host = parsedUrl.hostname.toLowerCase();
      const pathname = parsedUrl.pathname;

      // Convert http to https for validation
      let proto = protocol;
      if (proto === "http:") {
        proto = "https:";
      }
      if (proto !== "https:") {
        return false;
      }

      return (
        (/^(www\.)?instagram\.com$/.test(host) &&
          (/^\/(?:[a-zA-Z0-9_.]+\/)?reel\/[a-zA-Z0-9_-]+\/?$/.test(pathname) ||
            /^\/(?:[a-zA-Z0-9_.]+\/)?reels\/[a-zA-Z0-9_-]+\/?$/.test(
              pathname,
            ) ||
            /^\/(?:[a-zA-Z0-9_.]+\/)?p\/[a-zA-Z0-9_-]+\/?$/.test(pathname))) ||
        // YouTube Shorts
        (/^(www\.)?youtube\.com$/.test(host) &&
          /^\/shorts\/[a-zA-Z0-9_-]{11}\/?$/.test(pathname)) ||
        (/^(www\.)?youtu\.be$/.test(host) &&
          /^\/shorts\/[a-zA-Z0-9_-]{11}\/?$/.test(pathname)) ||
        // X / Twitter status (with media segments)
        (/^(www\.)?(x\.com|twitter\.com)$/.test(host) &&
          /^\/[A-Za-z0-9_]{1,15}\/status\/\d+(\/(photo|video|gif)(\/[1-4])?)?\/?$/.test(
            pathname,
          )) ||
        // TikTok video
        (/^(www\.)?tiktok\.com$/.test(host) &&
          (/^\/@[\w.-]+\/video\/\d+\/?$/.test(pathname) ||
            /^\/@[\w.-]+\/photo\/\d+\/?$/.test(pathname) ||
            /^\/t\/[\w-]+\/?$/.test(pathname)))
      );
    } catch (e) {
      return false;
    }
  };

  return (
    <div className="font-inter relative min-h-screen overflow-hidden bg-[#EFF2FC] bg-cover bg-no-repeat p-16 text-gray-800">
      {hasPermission && (
        <button
          onClick={() => navigate("/socialglass/assigncohort")}
          className="text-black-500 absolute left-4 top-4 z-[999] flex items-center transition hover:text-blue-700"
          aria-label="Go to Assign Cohort"
        >
          <MdArrowBack size={35} />
        </button>
      )}

      <button
        onClick={handleClose}
        className="absolute right-4 top-4 z-[999] text-[#ED2C08] transition hover:text-red-700"
        aria-label="Log out"
      >
        <img src={logoutIcon} alt="Log out" className="h-8 w-8" />
      </button>

      {/* Background Images */}
      <img
        src={Elipse1}
        alt="Elipse1"
        className="pointer-events-none absolute right-0 top-0 h-auto w-[15rem] md:w-[30rem] lg:w-[70rem]"
      />
      <img
        src={Elipse2}
        alt="Elipse2"
        className="pointer-events-none absolute bottom-0 left-0 h-auto w-[8rem] md:w-[15rem] lg:w-[35rem]"
      />

      {/* Content */}
      <h2 className="mb-6 text-3xl font-semibold tracking-tight">
        Submit Your Post
      </h2>

      {/* Prompt Selection */}
      <div className="mb-8 flex flex-wrap justify-start gap-3 font-sans text-sm text-gray-800">
        {prompts.length === 0 ? (
          <p className="text-sm text-gray-500">No prompts available</p>
        ) : (
          prompts.map((prompt) => {
            const isSelected = selectedPrompt === prompt.promptID;
            return (
              <button
                key={prompt.promptID}
                onClick={() => handlePromptClick(prompt.promptID)}
                className={`flex cursor-pointer items-center justify-center rounded-[28px] border px-4 py-2 transition duration-150 hover:scale-[1.01] hover:shadow-md ${
                  isSelected
                    ? "border-[#ED2C08] bg-[#FAD8D8]"
                    : "border-gray-300 bg-white"
                }`}
              >
                <span
                  className="text-sm font-semibold"
                  style={{ color: isSelected ? "#ED2C08" : "#1F2937" }}
                >
                  Prompt {prompt.promptID}
                </span>
              </button>
            );
          })
        )}
      </div>

      {/* Display the selected prompt details */}
      {selectedPromptDetails && (
        <div className="ml-2 mt-8 w-full max-w-full overflow-hidden">
          <h3 className="mb-4 break-words text-left text-base font-semibold sm:text-lg md:text-xl">
            {selectedPromptDetails.cohortName}
          </h3>
          <p className="whitespace-pre-wrap break-words text-left text-xs text-gray-700 sm:text-sm md:text-base">
            {selectedPromptDetails.promptText}
          </p>
          {selectedPromptDetails.defaultCategory && (
            <p className="mt-2 text-left text-xs italic text-gray-500 sm:text-sm">
              Default Category: {selectedPromptDetails.defaultCategory}
            </p>
          )}
        </div>
      )}

      {/* Post URL Submission */}
      <div className="mx-auto mt-8 w-full max-w-lg rounded-[12px] bg-white p-6 shadow-[0px_20px_24px_-4px_rgba(10,13,18,0.08),_0px_8px_8px_-4px_rgba(10,13,18,0.03)]">
        <form onSubmit={handleSubmit} className="m-0 space-y-4">
          <h2 className="mb-2 text-left text-xl font-semibold text-gray-900">
            Upload the Link
          </h2>
          <p className="mb-2 mt-0 text-left text-sm text-gray-600">
            Please provide the URL for the submission prompt.
          </p>

          {/* Input + Upload Button */}
          <div className="mb-4 flex w-full flex-nowrap items-center gap-2">
            <input
              type="url"
              placeholder="Enter the Link"
              value={newURL}
              onChange={(e) => setNewURL(e.target.value)}
              className="h-[42px] min-w-0 flex-grow rounded-[8px] border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
            />
            <button
              type="button"
              onClick={handleUpload}
              className="flex h-[42px] flex-shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-[8px] border border-white bg-[#ED2C08] px-4 text-sm font-semibold leading-none text-white shadow-[0px_1px_2px_rgba(5,32,81,0.05)] transition hover:opacity-90"
            >
              Upload
            </button>
          </div>

          {/* List of Added URLs */}
          {postURLs.map((url, index) => (
            <div
              key={index}
              className="mt-2 flex flex-nowrap items-center justify-between gap-2 pb-0"
              style={{ borderBottom: "1px solid #FAE3FC" }}
            >
              <div className="flex min-w-0 flex-1 items-center">
                <img
                  src={Globe}
                  alt="Globe Icon"
                  className="mr-3 h-5 w-5 shrink-0 object-contain"
                />
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="max-w-full truncate break-words text-sm text-black underline"
                >
                  {url}
                </a>
              </div>
              <button
                type="button"
                onClick={() => handleRemoveURL(index)}
                className="flex shrink-0 items-center justify-center p-2"
              >
                <img
                  src={Icon}
                  alt="Remove Icon"
                  className="mr-3 h-4 w-4 shrink-0 object-contain"
                />
              </button>
            </div>
          ))}

          {/* Action Buttons */}
          <div className="mt-10 flex w-full flex-nowrap gap-3">
            <button
              type="button"
              onClick={handleCancel}
              className="border-gray flex h-[42px] w-1/2 items-center justify-center gap-2 rounded-[8px] border bg-white px-4 text-sm font-semibold leading-none text-black shadow-[0px_1px_2px_rgba(5,32,81,0.05)] transition hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex h-[42px] w-1/2 items-center justify-center gap-2 rounded-[8px] border border-white bg-[#ED2C08] px-4 text-sm font-semibold leading-none text-white shadow-[0px_1px_2px_rgba(5,32,81,0.05)] transition hover:opacity-90"
            >
              Submit
            </button>
          </div>
        </form>

        {submitStatus && (
          <div
            className={`mt-6 rounded-lg border p-3 text-sm ${
              isError
                ? "border-red-300 bg-red-100 text-red-700"
                : "border-green-300 bg-green-100 text-green-700"
            }`}
          >
            {submitStatus}
          </div>
        )}

        {successfullySubmittedPosts.length > 0 && (
          <div className="mt-6 rounded-lg border border-green-300 bg-green-50 p-3">
            <h4 className="mb-2 text-sm font-semibold text-green-700">
              Successfully Added:
            </h4>
            <ul className="list-inside list-disc text-sm text-green-700">
              {successfullySubmittedPosts.map((url, idx) => (
                <li key={idx} className="truncate">
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    {url}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        {duplicatePosts.length > 0 && (
          <div className="mt-6 rounded-lg border border-yellow-300 bg-yellow-50 p-3">
            <h4 className="mb-2 text-sm font-semibold text-yellow-700">
              Skipped (Already Submitted Before):
            </h4>
            <ul className="list-inside list-disc text-sm text-yellow-700">
              {duplicatePosts.map((url, idx) => (
                <li key={idx} className="truncate">
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    {url}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
        {invalidPosts.length > 0 && (
          <div className="mt-6 rounded-lg border border-red-500 bg-yellow-50 p-3">
            <h4 className="mb-2 text-sm font-semibold text-red-700">
              Skipped (Already Submitted Before):
            </h4>
            <ul className="list-inside list-disc text-sm text-red-700">
              {invalidPosts.map((url, idx) => (
                <li key={idx} className="truncate">
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    {url}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default PromptSubmission;
